import type { CachedEmployeePatterns } from '@lms/shared-types'
import { ModalDrawerFlow } from '../templates'
import { AlgoBadge } from '../primitives/AlgoBadge'
import { Card } from '../primitives/Card'
import { Bar } from '../primitives/Bar'
import { Severity } from '../primitives/Severity'
import { PatternCard, PATTERN_META, severityToKind, severityColor } from './PatternCard'
import { HealthBarChip } from '../primitives/HealthBarChip'
import { flags } from '../../lib/feature-flags'
import { fmtDate } from '../../lib/format'

interface EmployeePatternsModalProps {
  open: boolean
  onClose: () => void
  employeeId: string
  employeeName: string
  teamName: string
  data: CachedEmployeePatterns
  onDraftConversation: () => void
}

export function EmployeePatternsModal({
  open,
  onClose,
  employeeId: _employeeId,
  employeeName,
  teamName,
  data,
  onDraftConversation,
}: EmployeePatternsModalProps): JSX.Element | null {
  if (!open) return null

  const patternCount = data.patterns.length
  const flaggedCount = data.patterns.filter(p => p.severity !== 'low').length

  return (
    <ModalDrawerFlow
      open={open}
      onClose={onClose}
      mode="modal"
      maxWidth={720}
      header={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)' }}>
              {employeeName}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {teamName}{teamName ? ' · ' : ''}{patternCount} pattern{patternCount === 1 ? '' : 's'} detected
            </div>
            {data.healthBar && data.healthBar.band !== 'low' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Outlook score:</span>
                <HealthBarChip
                  band={data.healthBar.band}
                  score={data.healthBar.score}
                  contributingPatterns={data.healthBar.contributingPatterns ?? []}
                  size="md"
                  testId="employee-patterns-modal-health-bar"
                />
              </div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              width: 28, height: 28, minWidth: 44, minHeight: 44,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--border)', borderRadius: 6,
              background: 'transparent', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16,
            }}
          >
            ✕
          </button>
        </div>
      }
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--surface)', color: 'var(--text-2)',
              fontWeight: 600, cursor: 'pointer', minHeight: 44,
            }}
          >
            Dismiss
          </button>
          {flags.aiConversationDrafter && flaggedCount > 0 ? (
            <button
              type="button"
              onClick={onDraftConversation}
              style={{
                padding: '8px 16px', background: 'var(--steel-blue)', color: 'var(--surface)',
                border: 'none', borderRadius: 8, fontWeight: 600,
                cursor: 'pointer', minHeight: 44,
              }}
            >
              Draft 1:1 message
            </button>
          ) : null}
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {data.detectedAt ? (
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
            Last detected: {fmtDate(data.detectedAt)}
          </div>
        ) : null}

        {data.patterns.length === 0 ? (
          <Card padding={32} style={{ textAlign: 'center', background: 'var(--success-bg)', borderColor: 'var(--success-line)' }}>
            <div style={{ color: 'var(--success-fg)', fontSize: 14 }}>
              No notable patterns for {employeeName}.
            </div>
          </Card>
        ) : (
          <ul role="list" data-testid="employee-patterns-list" style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 0, margin: 0, listStyle: 'none' }}>
            {data.patterns.map((p) => (
              <li key={p.kind}>
                <PatternDetailCard pattern={p} />
              </li>
            ))}
          </ul>
        )}

        {flaggedCount > 0 ? (
          <Card padding={16} style={{ background: 'var(--surface-2)', borderColor: 'var(--border)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginBottom: 4 }}>
              Suggested 1-on-1 conversation
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-2)' }}>
              {data.patterns
                .filter(p => p.severity !== 'low')
                .map(p => (
                  <li key={p.kind}>{p.recommendation}</li>
                ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </ModalDrawerFlow>
  )
}

/** Inline pattern card for the detail modal — same visual as PatternCard but without action buttons. */
function PatternDetailCard({ pattern }: { pattern: CachedEmployeePatterns['patterns'][number] }): JSX.Element {
  const meta = PATTERN_META[pattern.kind as keyof typeof PATTERN_META]
  const sev = pattern.severity
  const color = severityColor(sev)
  const pct = Math.min(100, Math.round((pattern.evidence.count / 6) * 100))

  return (
    <Card padding={16} style={{ borderLeft: `4px solid ${color}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlgoBadge />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {meta?.headline ?? pattern.kind}
          </span>
        </div>
        <Severity kind={severityToKind(sev)}>
          {pattern.evidence.count} {pattern.evidence.count === 1 ? 'leave' : 'leaves'}
        </Severity>
      </div>
      <Bar value={pct} color={color} />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 6, fontSize: 13, color: 'var(--text-2)' }}>
        <span>{pattern.possibleSignal}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
        Window: {pattern.evidence.windowDays} days · {pattern.evidence.sampleLeaveIds.length} sample{pattern.evidence.sampleLeaveIds.length === 1 ? '' : 's'}
      </div>
    </Card>
  )
}
